import { LIFE_EVENT_TYPES, LifeEventType } from "@/src/models/post.model";
import { useTheme } from "@/src/styles/theme-provider";
import { CloseCircle } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import { Modal, Platform, Pressable, Text, TextInput, View } from "react-native";

const LIFE_EVENT_EMOJIS: Record<LifeEventType, string> = {
  Promotion: "🎉",
  Education: "🎓",
  Relationship: "❤️",
  NewJob: "💼",
  Moved: "🏠",
  General: "⭐",
};

type LifeUpdatePickerProps = Readonly<{
  visible: boolean;
  selectedType?: LifeEventType;
  selectedDate?: string;
  onSelectType: (type: LifeEventType) => void;
  onSelectDate: (date: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}>;

const today = () => new Date().toISOString().split("T")[0];

const LifeUpdatePicker = ({
  visible,
  selectedType,
  selectedDate,
  onSelectType,
  onSelectDate,
  onConfirm,
  onClose,
}: LifeUpdatePickerProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View
        style={themeVars as any}
        className="flex-1 items-center justify-center bg-black/40"
      >
        <View className="bg-chalk rounded-2xl w-[90%] max-w-sm p-5 gap-4">
          <View className="flex-row justify-between items-center">
            <Text className="text-base font-semibold text-onyx">
              {t("LABEL_LIFE_UPDATE_PICKER_TITLE")}
            </Text>
            <Pressable onPress={onClose} className="p-1 hover:bg-ash-3 hover:rounded-full">
              <CloseCircle size={20} color={themeVars["--color-ash-1"] as string} />
            </Pressable>
          </View>

          <View className="flex-row flex-wrap gap-2">
            {LIFE_EVENT_TYPES.map((type) => (
              <Pressable
                key={type}
                onPress={() => onSelectType(type)}
                className={`flex-row items-center gap-1 px-3 py-2 rounded-full border ${
                  selectedType === type
                    ? "bg-primary border-primary"
                    : "bg-chalk border-ash-3 hover:bg-ash-3"
                }`}
              >
                <Text>{LIFE_EVENT_EMOJIS[type]}</Text>
                <Text
                  className={`text-xs font-medium ${
                    selectedType === type ? "text-white" : "text-onyx"
                  }`}
                >
                  {t(`LABEL_LIFE_EVENT_${type.toUpperCase()}`)}
                </Text>
              </Pressable>
            ))}
          </View>

          <View className="gap-1">
            <Text className="text-xs text-gray-dark">{t("LABEL_LIFE_UPDATE_DATE")}</Text>
            {Platform.OS === "web" ? (
              <input
                type="date"
                max={today()}
                value={selectedDate ?? ""}
                onChange={(e) => onSelectDate(e.target.value)}
                style={{
                  border: "1px solid var(--color-ash-3)",
                  borderRadius: 8,
                  padding: "6px 12px",
                  fontSize: 13,
                  color: themeVars["--color-onyx-default"] as string,
                  background: "transparent",
                  outline: "none",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              />
            ) : (
              <TextInput
                value={selectedDate ?? ""}
                onChangeText={onSelectDate}
                placeholder="YYYY-MM-DD"
                placeholderTextColor={themeVars["--color-ash-1"] as string}
                style={{ color: themeVars["--color-onyx-default"] as string }}
                className="border border-ash-3 rounded-lg px-3 py-2 text-sm"
              />
            )}
          </View>

          <Pressable
            onPress={onConfirm}
            disabled={!selectedType}
            className={`py-2 rounded-lg items-center ${
              selectedType ? "bg-primary" : "bg-ash-2"
            }`}
          >
            <Text className="text-white text-sm font-medium">
              {t("BTN_CONFIRM")}
            </Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
};

export default LifeUpdatePicker;
